import { createKeyword, saveEmissionFactors, saveKpis } from "../../kpiGridApi";
import { buildSavePayload, factorsToWire, kpiIdsByTitle } from "../helpers";

const KPI_BATCH = 100;
const FACTOR_BATCH = 200;

const chunks = (list, size) => {
  const out = [];
  for (let i = 0; i < list.length; i += size) out.push(list.slice(i, i + size));
  return out;
};

/**
 * Saves a plan from planImport through the grid's own endpoints: KPI rows
 * first (new ones get their ids), then factor lists. Each batch is its own
 * transaction, so a failure part-way says exactly how much landed.
 *
 * Tag values with no `riu_keywords` pair are filed first, so the vocabulary
 * has them whether or not the rest of the save goes through. A pair that
 * cannot be written is not fatal — the KPI's own tag does not depend on it.
 *
 * @param {ReturnType<import("./planImport").planImport>} plan
 * @param {{ financialYearId: string|number, onProgress?: (message: string) => void }} options
 * @returns {Promise<{ created: number, updated: number, factorKpis: number, keywords: number }>}
 */
export const applyImport = async (plan, { financialYearId, onProgress = () => { } }) => {
  let keywords = 0;
  if (plan.newKeywords?.length) {
    onProgress(`Adding ${plan.newKeywords.length} tag pairs…`);
    const results = await Promise.all(
      plan.newKeywords.map(({ keywordType, keywordValue }) =>
        createKeyword(keywordType, keywordValue, { silent: true }).catch(() => ({ isSuccess: false })),
      ),
    );
    keywords = results.filter((res) => res?.isSuccess !== false).length;
  }

  const rows = [...plan.updates.map((u) => u.row), ...plan.creates.map((c) => c.values)];
  const patches = Object.fromEntries(plan.updates.map((u) => [u.row.id, u.patch]));
  const idsByTitle = new Map();
  let savedRows = 0;

  const fail = (reason, stage) => {
    const done = [
      savedRows ? `${savedRows} of ${rows.length} KPIs saved` : null,
      stage === "factors" ? "no factors saved from this batch on" : null,
    ].filter(Boolean);
    throw new Error(done.length ? `${reason} — ${done.join("; ")}` : `${reason} — nothing was saved`);
  };

  for (const batch of chunks(rows, KPI_BATCH)) {
    onProgress(`Saving KPIs ${savedRows + 1}–${savedRows + batch.length} of ${rows.length}…`);
    const res = await saveKpis(buildSavePayload({ rows: batch, patches, moduleId: null, financialYearId }));
    if (!res || res.isSuccess === false) fail(res?.data?.error || "The KPI save was rejected", "kpis");
    kpiIdsByTitle(res.data?.data).forEach((id, title) => idsByTitle.set(title, id));
    savedRows += batch.length;
  }

  const items = plan.factorItems.map((item) => {
    const kpiId = item.questionId ?? idsByTitle.get(item.title.trim().toLowerCase());
    if (kpiId == null) fail(`Could not find the new KPI "${item.title}" to attach its factors`, "factors");
    return { kpi_id: kpiId, factors: factorsToWire(item.factors) };
  });

  let savedFactors = 0;
  for (const batch of chunks(items, FACTOR_BATCH)) {
    onProgress(`Saving emission factors for ${savedFactors + batch.length} of ${items.length} KPIs…`);
    const res = await saveEmissionFactors(batch);
    if (!res || res.isSuccess === false) fail(res?.data?.error || "The emission factors were rejected", "factors");
    savedFactors += batch.length;
  }

  return { created: plan.creates.length, updated: plan.updates.length, factorKpis: items.length, keywords };
};
